import { useCallback, useEffect, useLayoutEffect } from 'react';

import { useDispatch } from '#core/store';

import { getMediaQuery } from '#component/media-query/slice';

/* Media queries */

enum EBreakpoints {
    MobileMaxWidth = 767,
    TabletMinWidth = 768,
    TableMaxWidth = 1105,
    DesktopSmallMinWidth = 1106,
    DesktopSmallMaxWidth = 1365,
    DesktopMinWidth = 1366,
    DesktopMaxWidth = 1920,
    DesktopBigMinWidth = 1921
}

const mediaMatches = {
    isMobile: window.matchMedia(
        `(max-width: ${EBreakpoints.MobileMaxWidth}px)`
    ),
    isTablet: window.matchMedia(
        `(min-width: ${EBreakpoints.TabletMinWidth}px) and (max-width: ${EBreakpoints.TableMaxWidth}px)`
    ),
    isDesktopSmall: window.matchMedia(
        `(min-width: ${EBreakpoints.DesktopSmallMinWidth}px) and (max-width: ${EBreakpoints.DesktopSmallMaxWidth}px)`
    ),
    isDesktopMedium: window.matchMedia(
        `(min-width: ${EBreakpoints.DesktopMinWidth}px) and (max-width: ${EBreakpoints.DesktopMaxWidth}px)`
    ),
    isDesktopBig: window.matchMedia(
        `(min-width: ${EBreakpoints.DesktopBigMinWidth}px)`
    ),
    isDesktop: window.matchMedia(
        `(min-width: ${EBreakpoints.DesktopSmallMinWidth}px)`
    )
};

export const useGetMediaQuery = () => {
    const dispatch = useDispatch();

    const { isMobile, isTablet, isDesktopSmall, isDesktopMedium, isDesktopBig, isDesktop } = mediaMatches;

    useLayoutEffect(() => {
        const currentMediaQuery = {
            isMobile       : isMobile.matches,
            isTablet       : isTablet.matches,
            isDesktop      : isDesktop.matches,
            isDesktopSmall : isDesktopSmall.matches,
            isDesktopMedium: isDesktopMedium.matches,
            isDesktopBig   : isDesktopBig.matches
        };

        dispatch(getMediaQuery.actions.set(currentMediaQuery));
    }, [
        dispatch,
        isMobile.matches,
        isTablet.matches,
        isDesktop.matches,
        isDesktopSmall.matches,
        isDesktopMedium.matches,
        isDesktopBig.matches
    ]);

    const onUpdateMediaQuery = useCallback((type: string) => (e: MediaQueryListEvent) => {
        dispatch(getMediaQuery.actions.set({ [type]: e.matches }));
    }, [dispatch]);

    useEffect(() => {
        isMobile.addEventListener('change', onUpdateMediaQuery('isMobile'));
        isTablet.addEventListener('change', onUpdateMediaQuery('isTablet'));
        isDesktopSmall.addEventListener('change', onUpdateMediaQuery('isDesktopSmall'));
        isDesktopMedium.addEventListener('change', onUpdateMediaQuery('isDesktopMedium'));
        isDesktopBig.addEventListener('change', onUpdateMediaQuery('isDesktopBig'));
        isDesktop.addEventListener('change', onUpdateMediaQuery('isDesktop'));

        return () => {
            isMobile.removeEventListener('change', onUpdateMediaQuery('isMobile'));
            isTablet.removeEventListener('change', onUpdateMediaQuery('isTablet'));
            isDesktopSmall.removeEventListener('change', onUpdateMediaQuery('isDesktopSmall'));
            isDesktopMedium.removeEventListener('change', onUpdateMediaQuery('isDesktopMedium'));
            isDesktopBig.removeEventListener('change', onUpdateMediaQuery('isDesktopBig'));
            isDesktop.removeEventListener('change', onUpdateMediaQuery('isDesktop'));
        };
    }, [isDesktop, isDesktopBig, isDesktopSmall, isDesktopMedium, isMobile, isTablet, onUpdateMediaQuery]);
};
